import {
	ApiResponse,
	AuthResponse,
	BlogPost,
	ContactMessage,
	CreateBlogPost,
	CreateContactMessage,
	CreateGalleryImage,
	CreateMatch,
	CreateTeamMember,
	GalleryImage,
	LoginCredentials,
	Match,
	PaginatedResponse,
	Registration,
	TeamMember,
} from "@/lib/types";
import { apiClient } from "./api-client";

// Auth Service
export const authService = {
	login: async (credentials: LoginCredentials) => {
		return apiClient.post<ApiResponse<AuthResponse>>(
			"/api/auth/login",
			credentials
		);
	},

	logout: () => {
		apiClient.clearToken();
	},

	isAuthenticated: (): boolean => {
		return !!apiClient.getToken();
	},
};

// Blog Service
export const blogService = {
	getAll: async (page = 1, limit = 10) => {
		return apiClient.get<PaginatedResponse<BlogPost>>(
			`/api/blog?page=${page}&limit=${limit}`
		);
	},

	getBySlug: async (slug: string) => {
		return apiClient.get<ApiResponse<BlogPost>>(`/api/blog/${slug}`);
	},

	create: async (data: CreateBlogPost) => {
		return apiClient.post<ApiResponse<BlogPost>>("/api/blog", data);
	},

	update: async (id: string, data: Partial<CreateBlogPost>) => {
		return apiClient.put<ApiResponse<BlogPost>>(`/api/blog/${id}`, data);
	},

	delete: async (id: string) => {
		return apiClient.delete<ApiResponse<void>>(`/api/blog/${id}`);
	},
};

// Contact Service
export const contactService = {
	getAll: async (page = 1, limit = 10, status?: string) => {
		const statusQuery = status ? `&status=${status}` : "";
		return apiClient.get<PaginatedResponse<ContactMessage>>(
			`/api/contact?page=${page}&limit=${limit}${statusQuery}`
		);
	},

	getById: async (id: string) => {
		return apiClient.get<ApiResponse<ContactMessage>>(`/api/contact/${id}`);
	},

	create: async (data: CreateContactMessage) => {
		return apiClient.post<ApiResponse<ContactMessage>>("/api/contact", data);
	},

	updateStatus: async (id: string, status: string) => {
		return apiClient.patch<ApiResponse<ContactMessage>>(
			`/api/contact/${id}/status`,
			{ status }
		);
	},

	delete: async (id: string) => {
		return apiClient.delete<ApiResponse<void>>(`/api/contact/${id}`);
	},
};

// Upload Service
export const uploadService = {
	uploadImage: async (file: File, folder: string = "images") => {
		const formData = new FormData();
		formData.append("file", file);
		formData.append("folder", folder);

		return apiClient.post<ApiResponse<{ url: string }>>(
			"/api/upload",
			formData,
			{ headers: { "Content-Type": "multipart/form-data" } }
		);
	},

	deleteImage: async (url: string) => {
		return apiClient.delete<ApiResponse<void>>("/api/upload", {
			data: { url },
		});
	},
};

// Gallery Service
export const galleryService = {
	getAll: async () => {
		return apiClient.get<ApiResponse<GalleryImage[]>>("/api/gallery");
	},

	create: async (data: CreateGalleryImage) => {
		return apiClient.post<ApiResponse<GalleryImage>>("/api/gallery", data);
	},

	update: async (id: string, data: Partial<CreateGalleryImage>) => {
		return apiClient.put<ApiResponse<GalleryImage>>(`/api/gallery/${id}`, data);
	},

	delete: async (id: string) => {
		return apiClient.delete<ApiResponse<void>>(`/api/gallery/${id}`);
	},
};

// Team Service
export const teamService = {
	getAll: async () => {
		return apiClient.get<ApiResponse<TeamMember[]>>("/api/team");
	},

	create: async (data: CreateTeamMember) => {
		return apiClient.post<ApiResponse<TeamMember>>("/api/team", data);
	},

	update: async (id: string, data: Partial<CreateTeamMember>) => {
		return apiClient.put<ApiResponse<TeamMember>>(`/api/team/${id}`, data);
	},

	delete: async (id: string) => {
		return apiClient.delete<ApiResponse<void>>(`/api/team/${id}`);
	},
};

// Match Service
export const matchService = {
	getUpcoming: async () => {
		return apiClient.get<ApiResponse<Match[]>>("/api/matches/upcoming");
	},

	getAll: async (page = 1, limit = 10) => {
		return apiClient.get<PaginatedResponse<Match>>(
			`/api/matches?page=${page}&limit=${limit}`
		);
	},

	create: async (data: CreateMatch) => {
		return apiClient.post<ApiResponse<Match>>("/api/matches", data);
	},

	update: async (id: string, data: Partial<CreateMatch>) => {
		return apiClient.put<ApiResponse<Match>>(`/api/matches/${id}`, data);
	},

	delete: async (id: string) => {
		return apiClient.delete<ApiResponse<void>>(`/api/matches/${id}`);
	},
};

// Registration Service
export const registrationService = {
	getAll: async (page = 1, limit = 10, status?: string) => {
		const statusQuery = status ? `&status=${status}` : "";
		return apiClient.get<PaginatedResponse<Registration>>(
			`/api/registrations?page=${page}&limit=${limit}${statusQuery}`
		);
	},

	getById: async (id: string) => {
		return apiClient.get<ApiResponse<Registration>>(`/api/registrations/${id}`);
	},

	create: async (data: FormData) => {
		return apiClient.post<ApiResponse<Registration>>(
			"/api/registrations",
			data,
			{
				headers: { "Content-Type": "multipart/form-data" },
			}
		);
	},

	updateStatus: async (id: string, status: string) => {
		return apiClient.patch<ApiResponse<Registration>>(
			`/api/registrations/${id}/status`,
			{ status }
		);
	},

	delete: async (id: string) => {
		return apiClient.delete<ApiResponse<void>>(`/api/registrations/${id}`);
	},
};

export const statsService = {
	getDashboardStats: async () => {
		return apiClient.get<
			ApiResponse<{
				blogPosts: number;
				gallery: number;
				team: number;
				matches: number;
				registrations: number;
				messages: number;
			}>
		>("/api/admin/stats");
	},
};
